import { useContext } from 'react';
import {
  NavigationContext,
  type NavigationProp,
  type NavigationState,
} from '@react-navigation/native';

// 브라운필드의 embeddedInBaseTabRoot=true 호스트 안에서 동작하는 screen들.
// 이 screen들 위에서는 native 탭바가 노출되므로 in-tree 바텀시트가 탭바를 덮지 못함.
// → 새 modal surface 경로로 띄워야 함.
const TAB_HOSTED_SCREEN_NAMES = new Set([
  'FragmentScreens',
  'GoodsRanking',
  'MarketRanking',
  'FragmentsOverView',
  'LikeFragment',
]);

/**
 * NavigationContext 없이 screen 이름만으로 native 탭바 호스팅 여부 판별.
 * App.tsx 같이 NavigationContainer 외부에서 분기할 때 사용.
 */
export const isTabHostedScreen = (screen: string | undefined): boolean => {
  return screen != null && TAB_HOSTED_SCREEN_NAMES.has(screen);
};

type RouteLike = { name: string; state?: { routes?: unknown } };

const hasFragmentScreens = (routes: RouteLike[] | undefined): boolean => {
  if (!routes) return false;
  return routes.some(route => {
    if (TAB_HOSTED_SCREEN_NAMES.has(route.name)) return true;
    const nested = route.state?.routes;
    if (Array.isArray(nested)) {
      return hasFragmentScreens(nested as RouteLike[]);
    }
    return false;
  });
};

const getRootState = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  navigation: NavigationProp<any>,
): NavigationState | undefined => {
  let current = navigation;
  let parent = current.getParent();
  while (parent) {
    current = parent;
    parent = current.getParent();
  }
  return current.getState();
};

/**
 * 현재 navigation stack 어딘가에 native 탭바 위에서 호스팅되는 screen이 있는지 확인합니다.
 * (`FragmentScreens`, `GoodsRanking`, `MarketRanking` 등 — `TAB_HOSTED_SCREEN_NAMES` 참조)
 *
 * 브라운필드에서 `embeddedInBaseTabRoot=true`로 호스팅되는 surface의 RN 트리는
 * native 탭바 위에서 잘려 있어서, 바텀시트 등 풀스크린이 필요한 UI는 별도 modal
 * surface(`BottomSheetHost`) 경유로 띄워야 합니다.
 *
 * NavigationContainer 외부(예: 웹, storybook)에서도 안전하게 호출 가능 — 그 경우 false 반환.
 */
export const useIsInFragmentScreens = (): boolean => {
  const navigation = useContext(NavigationContext);
  if (!navigation) return false;
  const rootState = getRootState(navigation);
  return hasFragmentScreens(rootState?.routes as RouteLike[] | undefined);
};
